'use client'

import Link from 'next/link'
import { useState } from 'react'
import { track } from '@/lib/analytics'
import { ordinal, type Service } from '@/lib/portfolio'

/**
 * The services, as rows that open.
 *
 * Closed, it is the same numbered index the rest of the site uses; open, it
 * shows the summary and what you get, with the way through to the full page.
 * One at a time, so the list stays a list.
 */
export function ServiceAccordion({ services }: { services: Service[] }) {
    const [open, setOpen] = useState<string | null>(null)

    return (
        <div className="border-t border-hair">
            {services.map((service, index) => {
                const isOpen = open === service.slug

                return (
                    <div key={service.slug} className="border-b border-hair last:border-b-0">
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(isOpen ? null : service.slug)

                                if (!isOpen) {
                                    track({ event: 'service_open', service_slug: service.slug })
                                }
                            }}
                            aria-expanded={isOpen}
                            className="grid w-full cursor-pointer grid-cols-[44px_1fr] items-baseline gap-6 border-0 bg-transparent px-gutter py-7 text-left font-sans text-fg transition-[padding-left,background] duration-500 ease-design hover:bg-hover hover:pl-[46px] max-sm:grid-cols-[26px_1fr] max-sm:gap-2.5"
                        >
                            <span className="font-mono text-[11px] text-faint">{ordinal(index)}</span>
                            <span className="flex items-baseline justify-between gap-8">
                                <span className="text-[clamp(28px,3.4vw,44px)] font-normal tracking-[-0.04em] max-sm:text-2xl">
                                    {service.title}
                                </span>
                                <span className="font-mono text-[15px] text-faint">{isOpen ? '−' : '+'}</span>
                            </span>
                        </button>

                        {isOpen ? (
                            <div className="grid grid-cols-[44px_1fr] gap-5 px-gutter pb-[34px] max-sm:grid-cols-1 max-sm:gap-0">
                                <span className="max-sm:hidden" />
                                <div className="grid animate-rise-in grid-cols-[7fr_5fr] gap-12 max-lg:grid-cols-1 max-lg:gap-7">
                                    <div>
                                        <p className="m-0 max-w-[52ch] text-[16.5px] leading-[1.7] text-dim">
                                            {service.summary}
                                        </p>
                                        <Link
                                            href={service.url}
                                            className="mt-[22px] inline-block font-mono text-[11.5px] text-accent"
                                        >
                                            bekijk {service.shortTitle} →
                                        </Link>
                                    </div>
                                    <ul className="m-0 flex list-none flex-col gap-[9px] p-0">
                                        {service.deliverables.map((point) => (
                                            <li
                                                key={point}
                                                className="border-t border-hair pt-[9px] font-mono text-xs text-dim"
                                            >
                                                {point}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )
            })}
        </div>
    )
}
